/**
 * DD-3780: points at a button, tab, link or field the help assistant names, by its
 * visible text: scrolls it into view and pulses an outline around it for a few seconds.
 * Elements inside the help drawer are never matched.
 */
const CANDIDATES = [
    'button',
    'a',
    '[role="tab"]',
    '[role="menuitem"]',
    '[role="button"]',
    'label',
    'legend',
    'th',
    'h1',
    'h2',
    'h3',
    'h4',
    '.MuiFormControlLabel-label',
    '.MuiInputLabel-root',
    '.MuiTypography-root',
].join(',');
/** A matched label stands for its whole field: the outline goes round the field. */
const FIELD_CONTAINERS = ['.MuiAutocomplete-root', '.MuiFormControlLabel-root', '.MuiFormControl-root', 'fieldset'];
const MAX_HIGHLIGHTS = 4;
const HIGHLIGHT_CLASS = 'denthub-assistant-highlight';
const HIGHLIGHT_MS = 4000;
const STYLE_ID = 'denthub-assistant-highlight-style';
const RETRY_MS = 250;
const MAX_WAIT_MS = 4000;

/** The outline colour, set per highlight: the theme's secondary colour. */
const COLOR_VARIABLE = '--denthub-assistant-highlight';

const normalise = (text: string): string => text.replace(/\s+/g, ' ').trim().toLowerCase();

const ensureStyle = (): void => {
    if (document.getElementById(STYLE_ID)) {
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        @keyframes denthub-assistant-pulse {
            0%, 100% { box-shadow: 0 0 0 3px var(${COLOR_VARIABLE}); }
            50% { box-shadow: 0 0 0 8px color-mix(in srgb, var(${COLOR_VARIABLE}) 25%, transparent); }
        }
        .${HIGHLIGHT_CLASS} {
            animation: denthub-assistant-pulse 1s ease-in-out 4;
            border-radius: 6px;
            position: relative;
            z-index: 2;
        }`;
    document.head.appendChild(style);
};

const isInsideHelpDrawer = (element: Element): boolean => !!element.closest('[data-help-drawer]');

/** What a user reads on the element: its text, or a field's placeholder or aria-label. */
const textsOf = (element: HTMLElement): string[] =>
    [element.textContent, element.getAttribute('placeholder'), element.getAttribute('aria-label')]
        .filter((text): text is string => !!text)
        .map(normalise)
        .filter(Boolean);

/** "Välj kliniker" and "Välj klinik" are the same field: one starts with the other, a few letters apart. */
const isCloseMatch = (text: string, wanted: string): boolean => {
    const [shorter, longer] = text.length < wanted.length ? [text, wanted] : [wanted, text];
    return shorter.length >= 4 && longer.startsWith(shorter) && longer.length - shorter.length <= 3;
};

/** A label stands for its field; anything else is highlighted as it is. */
const targetOf = (element: HTMLElement): HTMLElement => {
    if (!element.matches('label, legend, .MuiInputLabel-root, .MuiFormControlLabel-label, input, textarea')) {
        return element;
    }
    // The outermost kind first: a dropdown's inner form control is not the whole field.
    for (const container of FIELD_CONTAINERS) {
        const field = element.closest<HTMLElement>(container);
        if (field) {
            return field;
        }
    }
    return element;
};

/** The tab or menu entry the user is already on is rarely what an answer tells them to click. */
const isCurrentSelection = (element: HTMLElement): boolean =>
    element.getAttribute('aria-selected') === 'true' ||
    element.hasAttribute('aria-current') ||
    element.classList.contains('Mui-selected');

const isClickable = (element: HTMLElement): boolean => element.matches('button, a, [role="button"]');

/** Clickable things first, the current tab or menu entry last; otherwise page order. */
const rank = (element: HTMLElement): number => {
    if (isCurrentSelection(element)) {
        return 2;
    }
    return isClickable(element) ? 0 : 1;
};

/**
 * Every element whose visible text matches, best first: the best kind of match only
 * (exact, then close — one is the other plus a few letters — then a longer text starting
 * with the name), ranked so a button beats the selected tab with the same text. Visible only.
 */
export const findUiElements = (name: string, root: ParentNode = document): HTMLElement[] => {
    const wanted = normalise(name);
    if (!wanted) {
        return [];
    }
    const elements = Array.from(root.querySelectorAll<HTMLElement>(`${CANDIDATES},input,textarea`)).filter(
        (element) => !isInsideHelpDrawer(element) && element.getClientRects().length > 0,
    );
    const tiers = [
        (text: string) => text === wanted,
        (text: string) => isCloseMatch(text, wanted),
        (text: string) => text.startsWith(wanted) && text.length < wanted.length + 30,
    ];
    for (const matches of tiers) {
        const found = elements.filter((element) => textsOf(element).some(matches));
        // A button's inner text matches too: keep the outermost of each.
        const outermost = found.filter(
            (element) => !found.some((other) => other !== element && other.contains(element)),
        );
        if (outermost.length) {
            return Array.from(
                new Set(
                    outermost
                        .map((element) => ({ element, order: rank(element) }))
                        .sort((a, b) => a.order - b.order)
                        .map(({ element }) => targetOf(element)),
                ),
            );
        }
    }
    return [];
};

export const findUiElement = (name: string, root: ParentNode = document): HTMLElement | undefined =>
    findUiElements(name, root)[0];

export const highlightElement = (element: HTMLElement, color: string, scrollTo = true): void => {
    ensureStyle();
    element.style.setProperty(COLOR_VARIABLE, color);
    if (scrollTo) {
        element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
    element.classList.add(HIGHLIGHT_CLASS);
    setTimeout(() => element.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);
};

/**
 * Highlights the named elements found on the page (up to four, the first scrolled to),
 * retrying while a page that was just opened renders. Resolves whether any was found.
 */
const pickOne = (matches: HTMLElement[], names: string[]): HTMLElement[] =>
    matches.length ? [matches[nextMatchIndex(names, matches.length)]] : [];

/** Clicking the same name again soon after moves on to its next match on the page. */
const REPEAT_WINDOW_MS = 15000;
let lastClick: { name: string; at: number; index: number } | undefined;

const nextMatchIndex = (names: string[], matchCount: number): number => {
    const name = names.length === 1 ? normalise(names[0]) : '';
    const isRepeat = !!name && lastClick?.name === name && Date.now() - lastClick.at < REPEAT_WINDOW_MS;
    const index = isRepeat && lastClick ? (lastClick.index + 1) % matchCount : 0;
    lastClick = name ? { name, at: Date.now(), index } : undefined;
    return index;
};

export const highlightUiElement = (names: string[], color: string): Promise<boolean> =>
    new Promise((resolve) => {
        const started = Date.now();
        const attempt = () => {
            const found =
                names.length === 1
                    ? pickOne(findUiElements(names[0]), names)
                    : Array.from(
                          new Set(
                              names
                                  .map((name) => findUiElement(name))
                                  .filter((element): element is HTMLElement => !!element),
                          ),
                      ).slice(0, MAX_HIGHLIGHTS);
            if (found.length) {
                found.forEach((element, index) => highlightElement(element, color, index === 0));
                resolve(true);
            } else if (Date.now() - started < MAX_WAIT_MS) {
                setTimeout(attempt, RETRY_MS);
            } else {
                resolve(false);
            }
        };
        attempt();
    });
